import React from "react";
import {
  Globe,
  Smartphone,
  LayoutDashboard,
  Cog,
  Brain,
  Rocket,
  ArrowUpRight,
  Building2,
  Layers,
  Lightbulb,
} from "lucide-react";

const SERVICES = [
  {
    icon: Globe,
    title: "Website Development",
    copy: "Premium business websites engineered for credibility, clarity and measurable commercial outcomes.",
    span: "md:col-span-7",
    accent: "from-[#0A66FF]/40 to-transparent",
    highlight: true,
  },
  {
    icon: Smartphone,
    title: "Mobile App Development",
    copy: "Production-ready mobile applications — from concept and prototype through to iOS, Android and cross-platform launch.",
    span: "md:col-span-5",
    accent: "from-[#00D4FF]/30 to-transparent",
  },
  {
    icon: LayoutDashboard,
    title: "Web Applications & SaaS Platforms",
    copy: "Scalable platforms, dashboards and subscription-based products designed around the way your business actually operates.",
    span: "md:col-span-4",
    accent: "from-[#0A66FF]/30 to-transparent",
  },
  {
    icon: Cog,
    title: "Business Systems",
    copy: "Internal systems for operations, sales, staff, bookings, reporting and workflow automation — built around your real processes.",
    span: "md:col-span-4",
    accent: "from-white/5 to-transparent",
  },
  {
    icon: Brain,
    title: "AI-Powered Products",
    copy: "AI-assisted applications and automation-led product builds engineered to deliver real commercial value, not novelty.",
    span: "md:col-span-4",
    accent: "from-[#00D4FF]/30 to-transparent",
  },
  {
    icon: Rocket,
    title: "Product Studio",
    copy: "We also design, develop and launch our own company-owned apps, platforms and digital ventures — with the same standards we apply to client work.",
    span: "md:col-span-12",
    accent: "from-[#0A66FF]/40 via-[#00D4FF]/15 to-transparent",
    wide: true,
  },
];

const ENGAGEMENTS = [
  {
    icon: Building2,
    title: "Business Websites",
    copy: "High-quality websites designed for credibility, clarity and conversion.",
    micro: "Typically suited for established businesses looking to upgrade their digital presence.",
  },
  {
    icon: Layers,
    title: "Custom Applications",
    copy: "Bespoke systems, platforms and dashboards tailored to specific workflows.",
    micro: "Designed for businesses requiring more than off-the-shelf solutions.",
  },
  {
    icon: Lightbulb,
    title: "Product Development",
    copy: "Support for building and launching new digital products and app concepts.",
    micro: "From early-stage ideas to scalable platforms.",
  },
];

export default function Services() {
  return (
    <section
      id="services"
      data-testid="services-section"
      className="relative py-28 md:py-36"
    >
      <div className="max-w-[1280px] mx-auto px-6 lg:px-8">
        <SectionHeader
          eyebrow="Capabilities"
          title="What we build"
          subtitle="A focused range of services for serious businesses — from launch-ready websites to AI-driven product platforms."
        />

        <div className="mt-16 grid grid-cols-1 md:grid-cols-12 gap-5 md:gap-6">
          {SERVICES.map((s, i) => (
            <ServiceCard key={s.title} index={i} {...s} />
          ))}
        </div>

        {/* Typical engagement scope */}
        <div className="mt-24 md:mt-28">
          <div className="mk-reveal flex items-end justify-between flex-wrap gap-6">
            <div>
              <span className="text-[11px] uppercase tracking-[0.22em] text-[#00D4FF] font-medium">
                Engagement
              </span>
              <h3 className="mt-3 font-display text-3xl md:text-4xl tracking-tight text-white">
                Typical engagement scope
              </h3>
            </div>
            <p className="text-[14px] text-slate-500 max-w-md">
              Most projects fall into one of three categories. Each is approached
              with the same focus on quality and long-term value.
            </p>
          </div>

          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
            {ENGAGEMENTS.map((e, i) => (
              <EngagementCard key={e.title} index={i} {...e} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function ServiceCard({ icon: Icon, title, copy, span, accent, wide, highlight, index }) {
  return (
    <article
      data-testid={`service-card-${index}`}
      className={`mk-reveal mk-card mk-glow group relative overflow-hidden rounded-2xl p-7 md:p-9 ${span}`}
      style={{ transitionDelay: `${index * 60}ms` }}
    >
      <div
        className={`absolute -top-24 -right-24 h-64 w-64 rounded-full bg-gradient-to-br ${accent} opacity-60 blur-3xl pointer-events-none group-hover:opacity-90 transition-opacity duration-700`}
        aria-hidden
      />
      <div className="relative">
        <div className="inline-flex h-11 w-11 items-center justify-center rounded-xl border border-white/10 bg-white/[0.04]">
          <Icon className="h-5 w-5 text-[#00D4FF]" />
        </div>

        <h3 className={`mt-6 font-display text-2xl ${wide ? "md:text-3xl" : ""} text-white tracking-tight`}>
          {title}
        </h3>
        <p className="mt-3 text-[15px] leading-relaxed text-slate-400 max-w-xl">
          {copy}
        </p>

        {highlight && (
          <div className="mt-7 flex items-center gap-3 text-sm text-slate-300">
            <span className="font-mono text-xs text-slate-500">01</span>
            <span className="h-px w-16 bg-white/15" />
            <span>Designed to convert. Built to last.</span>
          </div>
        )}

        {wide && (
          <div className="mt-8 flex flex-wrap gap-2">
            {["Owned products", "Bespoke ventures", "Long-term builds"].map((t) => (
              <span
                key={t}
                className="rounded-full border border-white/10 bg-white/[0.04] px-3 py-1 text-xs text-slate-300"
              >
                {t}
              </span>
            ))}
          </div>
        )}

        <div className="mt-7 inline-flex items-center gap-1.5 text-[13px] font-medium text-slate-300 group-hover:text-[#00D4FF] transition-colors">
          Learn more
          <ArrowUpRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
        </div>
      </div>
    </article>
  );
}

function EngagementCard({ icon: Icon, title, copy, micro, index }) {
  return (
    <div
      data-testid={`engagement-card-${index}`}
      className="mk-reveal mk-card rounded-2xl p-7 md:p-8 flex flex-col"
      style={{ transitionDelay: `${index * 80}ms` }}
    >
      <div className="inline-flex h-11 w-11 items-center justify-center rounded-xl border border-white/10 bg-gradient-to-br from-[#0A66FF]/20 to-transparent">
        <Icon className="h-5 w-5 text-[#00D4FF]" />
      </div>
      <h4 className="mt-6 font-display text-xl md:text-[22px] tracking-tight text-white">
        {title}
      </h4>
      <p className="mt-2.5 text-[15px] text-slate-400 leading-relaxed">{copy}</p>
      <div className="mt-6 pt-5 border-t border-white/[0.06]">
        <p className="text-[13px] text-slate-500 leading-relaxed italic">
          {micro}
        </p>
      </div>
    </div>
  );
}

export function SectionHeader({ eyebrow, title, subtitle, align = "left" }) {
  return (
    <div
      className={`mk-reveal flex flex-col ${
        align === "center" ? "items-center text-center" : "items-start"
      }`}
    >
      <span className="text-[11px] uppercase tracking-[0.22em] text-[#00D4FF] font-medium">
        {eyebrow}
      </span>
      <h2 className="mt-4 font-display text-4xl md:text-5xl lg:text-6xl tracking-tight text-white max-w-3xl leading-[1.04]">
        {title}
      </h2>
      {subtitle && (
        <p className="mt-5 text-lg text-slate-400 max-w-2xl leading-relaxed">
          {subtitle}
        </p>
      )}
    </div>
  );
}
